import { useMemo, useState } from 'react';
import Typography from '@mui/material/Typography';
import Avatar from 'boring-avatars';
import styled from '@mui/system/styled';
import { observer } from 'mobx-react-lite';
import ButtonBase from '@mui/material/ButtonBase';
import AccountModal from './AccountModal';
import type { AnyWallet } from '@/stores/Connectors';

const Root = styled(ButtonBase)(({ theme }) => ({
	display: 'flex',
	flexShrink: 0,
	justifyContent: 'space-between',
	alignItems: 'center',
	lineHeight: 'normal',
	backgroundColor: theme.palette.common.backgroundSecondary,
	'&:hover': {
		backgroundColor: theme.palette.common.backgroundTertiary ?? theme.palette.common.backgroundSecondary,
	},
	borderRadius: 12,
	padding: '0px 16px',
	height: 36,
}));

const ConnectButton = styled(ButtonBase)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	lineHeight: 'normal',
	border: `1px solid ${theme.palette.common.borderPrimary}`,
	color: theme.palette.common.textPrimary,
	'&:hover': {
		backgroundColor: theme.palette.common.backgroundSecondary,
	},
	borderRadius: 12,
	padding: '0px 16px',
	height: 36,
}));

type AccountButtonProps = {
	vmType: 'evm' | 'solana' | 'sui';
	label: string;
	wallet: AnyWallet | undefined;
	onConnect: () => void;
};

const AccountButton = observer(function AccountButton({ vmType, label, wallet, onConnect }: AccountButtonProps) {
	const [showModal, setShowModal] = useState(false);
	const address = wallet?.selectedAddress;

	const displayAddress = useMemo<string>(() => {
		if (address) {
			return `${address.slice(0, 6)}...${address.slice(address.length - 4)}`;
		}
		return '';
	}, [address]);

	if (!wallet?.isReady || !address) {
		return (
			<ConnectButton onClick={onConnect}>
				<Typography variant="appSmallRegular">
					Connect {label}
				</Typography>
			</ConnectButton>
		);
	}

	return (
		<>
			<Root onClick={() => setShowModal(true)}>
				<Typography variant="appSmallRegular" color="common.textPrimary" sx={{ mr: 1 }}>
					{displayAddress}
				</Typography>
				<Avatar
					size={20}
					name={address}
					variant="bauhaus"
					colors={['#FFE600', '#248EED', '#A8EEFF', '#A7BEFE', '#875AFF']}
				/>
			</Root>
			{showModal && (
				<AccountModal
					vmType={vmType}
					wallet={wallet}
					onClose={() => setShowModal(false)}
					onChangeWallet={() => {
						setShowModal(false);
						onConnect();
					}}
				/>
			)}
		</>
	);
});

export default AccountButton;
